<template>
  <div class="w-[250px]">
    <ul v-if="modelValue && modelValue.length > 0" class="mb-8">
      <li
        v-for="(file, i) in modelValue"
        :key="file.name"
        class="flex pl-8 pr-10 py-[9px] mb-5 border transition-all ease-in-out duration-250"
        :class="[
          uploadProgressByIndex(i) < 1.0
            ? `border-blue-500 bg-gradient-to-r from-blue-300 from-0% to-blue-50`
            : 'border-green-600 bg-green-100',
        ]"
        :style="{
          // Basically the only way to have hard edges in gradients.
          // See https://github.com/tailwindlabs/tailwindcss/discussions/3682#discussioncomment-3332715
          '--tw-gradient-stops': `var(--tw-gradient-from) ${
            uploadProgressByIndex(i) * 100
          }%, var(--tw-gradient-to) ${uploadProgressByIndex(i) * 100}%`,
        }"
      >
        <span class="truncate">
          {{ file.name.split('.').slice(0, -1).join('.') }}
        </span>
        <span class="shrink-0">
          .{{ file.name.split('.').splice(-1).at(0) }}
        </span>
        <button class="ml-5" @click="removeFile(i)">
          <SpriteSymbol name="close" class="size-20" aria-hidden="true" />
        </button>
      </li>
    </ul>

    <button
      class="w-[250px] p-20 border border-dashed border-gray-300 bg-white hover:bg-blue-50 hover:border-blue-500 transition-all ease-in-out duration-250 rounded cursor-pointer flex flex-col gap-10 items-center"
      :class="{
        '!bg-blue-50 !border-blue-500': draggingOver,
      }"
      @click="openFileDialog"
      @drop="onFileDrop"
      @dragover="draggingOver = true"
      @dragenter="draggingOver = true"
      @dragleave="draggingOver = false"
    >
      {{
        multiple
          ? $texts('fileInput.dropHereMultiple', 'Dateien hier droppen oder')
          : $texts('fileInput.dropHereSingle', 'Datei hier droppen oder')
      }}

      <span class="inline-link">
        {{
          multiple
            ? $texts('fileInput.selectMultiple', 'Dateien auswählen')
            : $texts('fileInput.selectSingle', 'Datei auswählen')
        }}
      </span>
    </button>
    <span v-if="hasUserSelectedWrongFileType" class="text-red-800">
      {{
        $texts(
          'fileInput.wrongFileType',
          'Eine oder mehrere Dateien sind im falschen Dateiformat. Sie wurden entfernt.',
        )
      }}
    </span>
    <input
      :id="id"
      ref="internalInput"
      type="file"
      :name="name"
      :multiple="multiple"
      :accept="accept"
      hidden
      @change="onFileInput"
    />
  </div>
</template>

<script lang="ts" setup>
const props = defineProps<{
  id?: string
  name?: string
  required?: boolean
  accept?: string
  multiple?: boolean
  modelValue?: FileList

  // Use this array to supply upload progress for each file given.
  // The order is important here, though: The template shows each bar
  // for the file in modelValue based on index. If a number is missing,
  // it simply assumes 0% progress to not indicate anything "wrong".
  uploadProgress?: number[]
}>()

const internalInput = ref<HTMLInputElement>()
const draggingOver = ref(false)
const hasUserSelectedWrongFileType = ref(false)

const emit = defineEmits(['update:modelValue'])

function uploadProgressByIndex(i: number): number {
  if (!props.uploadProgress) {
    return 0.0
  }

  if (!props.uploadProgress[i]) {
    return 0.0
  }

  return props.uploadProgress[i]
}

function openFileDialog(e: MouseEvent) {
  e.preventDefault()
  internalInput.value?.click()
}

function onFileDrop(e: DragEvent) {
  draggingOver.value = false
  e.preventDefault()

  if (!e.dataTransfer || e.dataTransfer.files.length === 0) {
    // No data being transferred, so no file upload.
    return
  }

  // One can always drop multiple files. That would circumvent the standard behaviour
  // if a file input doesn't allow "multiple"
  const initialFilesList = new DataTransfer()

  if (!props.multiple) {
    initialFilesList.items.add(e.dataTransfer.files[0])
  } else {
    for (const file of e.dataTransfer.files) {
      initialFilesList.items.add(file)
    }
  }

  // When dropping and manually setting, `accept` is circumvented,
  // so we filter the dropped files here.
  const acceptFilteredFilesList = new DataTransfer()

  if (props.accept && props.accept.length > 0) {
    // There's an `accept` restriction, so we filter.
    const accepts = props.accept.split(', ')

    for (const file of initialFilesList.files) {
      if (accepts.includes(file.type)) {
        acceptFilteredFilesList.items.add(file)
      }
    }

    // Show an error in case a single file has been kicked out of the list.
    hasUserSelectedWrongFileType.value =
      acceptFilteredFilesList.files.length < initialFilesList.files.length
  } else {
    // Add all of them, we don't have an `accept` restriction
    for (const file of initialFilesList.files) {
      acceptFilteredFilesList.items.add(file)
    }
  }

  internalInput.value!.files = acceptFilteredFilesList.files

  onFileInput()
}

function onFileInput() {
  emit('update:modelValue', internalInput.value!.files)
}

function removeFile(removeIndex: number) {
  const reducedDataFilesList = new DataTransfer()

  for (const [i, file] of Array.from(internalInput.value!.files!).entries()) {
    if (i === removeIndex) {
      continue
    }

    reducedDataFilesList.items.add(file)
  }

  internalInput.value!.files = reducedDataFilesList.files

  onFileInput()
}
</script>
